import { FC, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useRelativeTime } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible, Upsell } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  PerformanceEvaluationAnswerHeaderActionsDocument,
  PerformanceEvaluationAnswerHeaderFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FeedbackEntriesSidepanel } from 'modules/performance_feedbacks/components/FeedbackEntriesSidepanel'
import { PastCoursesSidepanel } from 'modules/performance_process/components/review/answer/PastCoursesSidepanel'
import { PastReviewsSidepanel } from 'modules/performance_process/components/review/answer/PastReviewsSidepanel'
import { getEvaluationType } from 'modules/performance_process/components/review/answer/helpers/getEvaluationType'
import { statusColor, tagLabel } from 'modules/performance_process/lib/answerTag'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

type Props = {
  evaluation: PerformanceEvaluationAnswerHeaderFragment
}

export const EvaluationAnswerHeader: FC<Props> = ({ evaluation }) => {
  const location = useLocation()
  const [showPastReviews, setShowPastReviews] = useState(false)
  const [showPastCourses, setShowPastCourses] = useState(false)
  const [showPastFeedbacks, setShowPastFeedbacks] = useState(false)
  const [showTrainingsUpsellModal, setShowTrainingsUpsellModal] = useState(false)

  const status = evaluation.status
  const type = evaluation.evaluationType
  const { title: evaluationTypeTitle, description } = type
    ? getEvaluationType(type)
    : { title: '', description: '' }
  const reviewIsFinished = evaluation.performanceReviewProcess?.status === 'finished'

  const targetAccessId = Number(evaluation?.targetAccess?.id)
  const deadline = evaluation.performanceReviewProcess?.endsAt

  const { formatted: formattedDeadline } = useRelativeTime({ date: deadline })

  const { company, canRequestUpsell } = useCurrentSession()

  const { data, loading } = useQuery(PerformanceEvaluationAnswerHeaderActionsDocument, {
    variables: { companyId: Number(company.id), targetAccessId },
  })

  const feedbacksFeatureEnabled =
    data?.features.featuresConnection.nodes.find((node) => node.name === 'dev_performance_feedback')
      ?.enabled ?? false

  const canRepublish = evaluation.answeredQuestionnaires?._policies?.republish?.can ?? false

  const isPublished = evaluation.status === 'published'

  const isRepublishModeFromNavigation = location.state?.isRepublishing === true

  const isRepublishing = canRepublish && isPublished && isRepublishModeFromNavigation

  const canUpsellTrainings =
    !loading && !data?.companies.trainingsModuleAvailability?.available && canRequestUpsell

  const canSeePastFeedbacks =
    feedbacksFeatureEnabled && !!data?.feedbacks.feedbackEntriesConnection.totalCount

  const canSeePastCourses = !!data?.trainings.trainingsConnection.totalCount
  const targetFullName = evaluation.targetAccess?.fullName
  const title = targetFullName ? `${targetFullName} - ${evaluationTypeTitle}` : evaluationTypeTitle

  return (
    <>
      <ResourceHeader
        title={title}
        description={description}
        status={{
          label: '',
          text: tagLabel(status, isRepublishing),
          variant: statusColor(status, isRepublishing),
        }}
        secondaryActions={[
          {
            label: i18n.t('performance_process.targets.see_past_courses.button'),
            icon: canUpsellTrainings ? Upsell : EyeVisible,
            onClick: () =>
              canUpsellTrainings ? setShowTrainingsUpsellModal(true) : setShowPastCourses(true),
            variant: canUpsellTrainings ? 'outlinePromote' : 'outline',
            isVisible: canUpsellTrainings || canSeePastCourses,
          },
          {
            label: i18n.t('performance_process.reviews.answer_evaluation.actions.see_past_reviews'),
            icon: EyeVisible,
            onClick: () => setShowPastReviews(true),
          },
          {
            label: i18n.t(
              'performance_process.reviews.answer_evaluation.actions.see_past_feedback'
            ),
            icon: EyeVisible,
            onClick: () => setShowPastFeedbacks(true),
            isVisible: canSeePastFeedbacks,
          },
        ]}
        metadata={compact([
          !!(!reviewIsFinished && deadline) && {
            label: i18n.t('performance_process.reviews.common.due_date'),
            value: {
              type: 'text',
              content: formattedDeadline || dateBackendDateTimeToUI(deadline),
            },
          },
          !!(evaluation.reviewerAccess && type !== 'self') && {
            label: i18n.t('performance_process.reviews.answer_evaluation.header.evaluator'),
            value: {
              type: 'avatar',
              text: evaluation.reviewerAccess.fullName,
              variant: {
                firstName: evaluation.reviewerAccess.firstName,
                lastName: evaluation.reviewerAccess.lastName,
                src: buildUrl(evaluation.reviewerAccess.avatar),
                type: 'person',
              },
            },
          },
          !!evaluation.targetAccess && {
            label: i18n.t('performance_process.reviews.answer_evaluation.header.evaluated'),
            value: {
              type: 'avatar',
              text: evaluation.targetAccess.fullName,
              variant: {
                firstName: evaluation.targetAccess.firstName,
                lastName: evaluation.targetAccess.lastName,
                src: buildUrl(evaluation.targetAccess.avatar),
                type: 'person',
              },
            },
          },
        ])}
      />
      {showPastReviews && evaluation.performanceReviewProcess && (
        <PastReviewsSidepanel
          open={showPastReviews}
          onClose={() => setShowPastReviews(false)}
          targetAccessId={targetAccessId}
          reviewProcessStartedAt={evaluation.performanceReviewProcess.startsAt}
        />
      )}
      {canSeePastCourses && (
        <PastCoursesSidepanel
          open={showPastCourses}
          onClose={() => setShowPastCourses(false)}
          targetAccessId={targetAccessId}
        />
      )}
      {canSeePastFeedbacks && (
        <FeedbackEntriesSidepanel
          receiverId={Number(targetAccessId)}
          isOpen={showPastFeedbacks}
          onClose={() => setShowPastFeedbacks(false)}
        />
      )}
      {showTrainingsUpsellModal && (
        <UpsellingKit.ProductModal
          isOpen
          onClose={() => setShowTrainingsUpsellModal(false)}
          requestSource="Performance - view past courses"
          moduleRequested={AvailableModules.trainings}
          requestReason={ValidReasons.MissingAddon}
          pageSource="performance_process"
        />
      )}
    </>
  )
}
